<template>
  <div ref="targetEl">
    <StyledOptionButton
      :class="[classes.button, 'relevant-button']"
      :href="url"
      :odds="odds"
      :disabled="disabled"
      :buttonId="buttonId"
      :name="name"
      :suspended="suspended"
      :winnings="localisedWinnings || undefined"
      @click="handleClick"
    />
  </div>
</template>

<script lang="ts">
import {
  injectBrand,
  BrandContext
} from "@fresh8/ad-data-composition/src/brand";
import { PropType, defineComponent, computed } from "vue";
import {
  BetData,
  Option,
  SportCompetitor,
  SportEvent
} from "@fresh8/types/src/betData";
import StyledOptionButton from "../../styled/WinButton.vue";
import { formatOdds } from "@fresh8/ad-data-composition/src/lib/formatOdds";
import usePotentialWinnings from "@fresh8/ad-data-composition/src/usePotentialWinnings";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import { injectCardNumber, useAnalytics } from "../../../hooks/useAnalytics";
import { AdTemplate, oddsTypeEnum } from "@fresh8/types/src/index";
import { injectBetData } from "@fresh8/ad-data-composition/src/betData";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";

const { useStyles } = createUseStyles({
  button: {
    height: "100%",
    flexDirection: "row",

    "& .name": {
      fontSize: "15px"
    },

    "& .button": {
      alignItems: "center"
    },

    "& .button div": {
      margin: 0
    }
  }
});

export default defineComponent({
  name: "RelevantButton",
  components: {
    StyledOptionButton
  },
  props: {
    option: {
      type: Object as PropType<Option>,
      required: false,
      default: undefined
    },
    isDraw: { type: Boolean, required: false, default: false },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const brand = injectBrand() as BrandContext;
    const adTemplate = computed(() => brand.adTemplate.value as AdTemplate);
    const betDataContext = injectBetData();
    const betData = computed(() => betDataContext?.betData.value as BetData);
    const buttonId = computed(() => adTemplate.value.market?.oddsButton);
    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      {
        optionId: props?.option?.id,
        marketId: props?.option?.marketID,
        cardNumber
      },
      "Market Insights Button"
    );

    const odds = computed(
      () =>
        (props.option &&
          formatOdds(
            props.option.odds,
            adTemplate.value.market.oddsButtonTemplate ||
              oddsTypeEnum.FRACTIONAL_ODDS
          )) ||
        "-"
    );

    const { localisedWinnings } = usePotentialWinnings(
      props.option?.odds || [0, 0]
    );

    const name = computed(() => {
      const template = getTranslation(window.navigator.language.toLowerCase())
        .MarketTexts.ToWin;

      const teamSide = props.option?.label;

      const event = betData.value.events[props.eventId] as
        | SportEvent
        | undefined;

      const competitor = event?.competitors[teamSide || ""] as
        | SportCompetitor
        | undefined;

      return (
        props.option &&
        template.replace("{name}", competitor?.name.short || "N/A")
      );
    });

    const { classesRef: classes } = useStyles(computed(() => ({})));

    const { targetEl } = composePopoverForm("Market Button", [
      {
        type: "button",
        get: () => adTemplate.value.market.oddsButton,
        set: (value: string) => (adTemplate.value.market.oddsButton = value)
      }
    ]);

    return {
      targetEl,
      handleClick: () => {
        if (!props.option?.id || props.option?.suspended) return;

        handleClick();
      },
      classes,
      odds,
      localisedWinnings,
      name,
      url: props.option?.betslipUrl,
      disabled: !props.option || props.option?.suspended,
      buttonId,
      suspended: props.option?.suspended
    };
  }
});
</script>
